import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { IProductV2, ICategoryItem } from '@care-portals/api-interfaces';

import * as ProductActions from './product.actions';

export const PRODUCT_FEATURE_KEY = 'product';

export interface ProductEntity extends IProductV2 {
  variants?: any[];
}

export interface State extends EntityState<ProductEntity> {
  loading: boolean;
  loadingCategories: boolean;
  categories: ICategoryItem[];
  selectedProductId: string | null;
  error?: any;
  variants: any[];
  loadingVariants: boolean;
  variantsError: any;
}

export interface ProductPartialState {
  readonly [PRODUCT_FEATURE_KEY]: State;
}

export function selectProductId(product: ProductEntity): string {
  return product._id;
}

export const productAdapter: EntityAdapter<ProductEntity> =
  createEntityAdapter<ProductEntity>({
    selectId: selectProductId
  });

export const initialState: State = productAdapter.getInitialState({
  loading: false,
  loadingCategories: false,
  categories: [],
  selectedProductId: null,
  error: null,
  variants: [],
  loadingVariants: false,
  variantsError: null
});

const ProductReducer = createReducer(
  initialState,

  /* ----------------------------- Load Products ------------------------------ */
  on(ProductActions.loadProducts, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(ProductActions.loadProductsSuccess, (state, { products }) =>
    productAdapter.setAll(products, {
      ...state,
      loading: false,
      error: null
    })
  ),
  on(ProductActions.loadProductsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  /* ----------------------------- Load Categories ------------------------------ */
  on(ProductActions.loadCategories, state => ({
    ...state,
    loadingCategories: true,
    error: null
  })),
  on(ProductActions.loadCategoriesSuccess, (state, { categories }) => ({
    ...state,
    loadingCategories: false,
    categories,
    error: null
  })),
  on(ProductActions.loadCategoriesFailure, (state, { error }) => ({
    ...state,
    loadingCategories: false,
    error
  })),

  /* ----------------------------- Get Product V2 ------------------------------ */
  on(ProductActions.getProductV2, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(ProductActions.getProductV2Success, (state, { product }) =>
    productAdapter.upsertOne(product, {
      ...state,
      loading: false,
      selectedProductId: product._id,
      error: null
    })
  ),
  on(ProductActions.getProductV2Failure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  on(ProductActions.loadProductVariants, state => ({
    ...state,
    loadingVariants: true,
    variantsError: null
  })),

  on(ProductActions.loadProductVariantsSuccess, (state, { variants }) => ({
    ...state,
    variants,
    loadingVariants: false
  })),

  on(ProductActions.loadProductVariantsFailure, (state, { error }) => ({
    ...state,
    variantsError: error,
    loadingVariants: false
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return ProductReducer(state, action);
}
